import { isAndroidTwaClient } from "./runtime.js";

const DEFAULT_STATUS = {
  bridgeReady: false,
  notificationPermission: "default",
  exactAlarmAllowed: null,
  exactAlarmSupported: true,
  exactAlarmPermissionRequested: false,
  installationId: "",
  token: "",
  appVersion: "",
  appBuild: "",
  packageName: "",
  device: null,
};

function parseMessage(raw) {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch (_) {
    return null;
  }
}

function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((nextResolve, nextReject) => {
    resolve = nextResolve;
    reject = nextReject;
  });
  return { promise, resolve, reject };
}

export function createAndroidTwaAdapter(options = {}) {
  const onStatusChange = typeof options.onStatusChange === "function" ? options.onStatusChange : null;
  const onNotificationOpen = typeof options.onNotificationOpen === "function" ? options.onNotificationOpen : null;
  let messagePort = null;
  let requestSeq = 0;
  let listening = false;
  const pending = new Map();
  const readyWaiters = new Set();
  let status = { ...DEFAULT_STATUS };

  function emitStatus() {
    if (onStatusChange) {
      onStatusChange({ ...status });
    }
  }

  function applyStatusPatch(patch = {}) {
    status = {
      ...status,
      ...patch,
      bridgeReady: !!messagePort,
    };
    emitStatus();
    if (status.bridgeReady) {
      flushReadyWaiters();
    }
  }

  function flushReadyWaiters() {
    if (!isReady()) {
      return;
    }

    for (const waiter of Array.from(readyWaiters)) {
      readyWaiters.delete(waiter);
      if (waiter.timer) {
        clearTimeout(waiter.timer);
      }
      waiter.resolve(true);
    }
  }

  function consumeBootstrapPort() {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      // The TWA bridge can hand us a port before the adapter finishes booting.
      const nextPort = window.__liftflowAndroidTwaBridgeBootstrap?.port;
      if (!(nextPort instanceof MessagePort)) {
        return false;
      }

      const origin = String(window.__liftflowAndroidTwaBridgeBootstrap?.origin || "");
      handleWindowMessage({
        ports: [nextPort],
        origin,
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  function send(type, payload = {}) {
    if (!messagePort) {
      return Promise.reject(new Error("android bridge is not ready"));
    }

    requestSeq += 1;
    const requestId = `android-bridge-${requestSeq}`;
    const deferred = createDeferred();
    pending.set(requestId, deferred);

    try {
      messagePort.postMessage(JSON.stringify({
        type,
        requestId,
        payload,
      }));
    } catch (error) {
      pending.delete(requestId);
      deferred.reject(error);
      return deferred.promise;
    }

    return deferred.promise;
  }

  function handlePortMessage(event) {
    const message = parseMessage(event?.data);
    if (!message || typeof message !== "object") {
      return;
    }

    const replyTo = String(message.replyTo || "").trim();
    if (replyTo && pending.has(replyTo)) {
      const deferred = pending.get(replyTo);
      pending.delete(replyTo);
      deferred?.resolve(message.payload ?? null);
    }

    if (message.type === "hello" || message.type === "status") {
      applyStatusPatch(message.payload || {});
      return;
    }

    if (message.type === "notification-open" && onNotificationOpen) {
      onNotificationOpen(message.payload || {});
    }
  }

  function handleWindowMessage(event) {
    const nextPort = event?.ports?.[0];
    if (!(nextPort instanceof MessagePort)) {
      return;
    }

    if (messagePort === nextPort) {
      return;
    }

    messagePort = nextPort;
    messagePort.onmessage = handlePortMessage;
    messagePort.start();
    applyStatusPatch({
      bridgeReady: true,
    });
  }

  function init() {
    if (listening || !isAndroidTwaClient() || typeof window === "undefined") {
      return;
    }
    window.addEventListener("message", handleWindowMessage);
    listening = true;
    consumeBootstrapPort();
  }

  async function waitForReady(timeoutMs = 3000) {
    if (isReady()) {
      return true;
    }

    init();
    consumeBootstrapPort();

    if (isReady()) {
      return true;
    }

    const waitMs = Number.isFinite(timeoutMs)
      ? Math.max(0, Number(timeoutMs) || 0)
      : 3000;
    if (waitMs <= 0 || typeof window === "undefined") {
      return isReady();
    }

    return await new Promise((resolve) => {
      const waiter = {
        timer: null,
        resolve(value) {
          readyWaiters.delete(waiter);
          if (waiter.timer) {
            clearTimeout(waiter.timer);
          }
          resolve(value);
        },
      };

      readyWaiters.add(waiter);
      waiter.timer = window.setTimeout(() => {
        waiter.resolve(false);
      }, waitMs);

      consumeBootstrapPort();
      if (isReady()) {
        flushReadyWaiters();
      }
    });
  }

  async function refreshStatus() {
    const nextStatus = await send("get-status", {});
    applyStatusPatch(nextStatus || {});
    return nextStatus || {};
  }

  return {
    init,
    waitForReady,
    isSupported() {
      return isAndroidTwaClient();
    },
    isReady() {
      return !!messagePort;
    },
    getStatus() {
      return { ...status };
    },
    async refreshStatus() {
      return refreshStatus();
    },
    async requestNotificationPermission() {
      const result = await send("request-notification-permission", {});
      applyStatusPatch(result || {});
      return result || {};
    },
    async requestExactAlarmPermission() {
      const result = await send("request-exact-alarm-permission", {});
      applyStatusPatch(result || {});
      return result || {};
    },
    async scheduleRestAlarm(payload) {
      return send("schedule-rest-alarm", payload);
    },
    async cancelRestAlarm(payload) {
      return send("cancel-rest-alarm", payload);
    },
  };
}
